import { Grid, Stack } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useEffect, useState } from "react";
import { useLiteralListQueryParamState } from "hooks/useLiteralListQueryParamState";
import { useIsAdmin, useTenant } from "hooks/settings";
import { MultiSelect } from "../../common/inputs/multiSelect";
import { TableHeader } from "../TableHeader";
import { GroupedPriceSeries } from "../types";
import { PricesPlot } from "./PricesPlot";
import { PricesTable } from "./PricesTable";
import { DateRangeSelector } from "components/common/DateRangeSelector";
import { ExcelDownloadButton } from "components/common/ExcelDownloadButton";
import { useDateRange } from "contexts/DateRangeContext";
import { backendApi, downloadAsExcel } from "src/store/api/enhancedApi";
import { PriceSourceName } from "src/store/api/generatedApi";

interface PricesTabProps {
  groupedPriceSeries: GroupedPriceSeries;
}

export const PricesTab = ({ groupedPriceSeries }: PricesTabProps) => {
  const isAdmin = useIsAdmin();
  const tenant = useTenant();
  const { t } = useTenantTranslation();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const [selectedPriceSources, setSelectedPriceSources] =
    useLiteralListQueryParamState<PriceSourceName>({
      paramName: "priceSources",
      permitted: groupedPriceSeries.availablePriceSources,
      localStorageKey: `${tenant}.strategyDashboard.prices.selectedPriceSources`,
      defaultValue: groupedPriceSeries.availablePriceSources,
    });

  const [selectedMaterials, setSelectedMaterials] =
    useLiteralListQueryParamState<string>({
      paramName: "materials",
      permitted: groupedPriceSeries.availableMaterialNames,
      localStorageKey: `${tenant}.strategyDashboard.prices.selectedMaterials`,
      defaultValue: groupedPriceSeries.availableMaterialNames,
    });

  useEffect(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets date on source change; user also clicks plot
    setSelectedDate(
      getDefaultSelectedDate(groupedPriceSeries, selectedPriceSources)
    );
  }, [groupedPriceSeries, selectedPriceSources]);

  return (
    <div>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        sx={{ mt: 2 }}
      >
        <DateRangeSelector />
        {isAdmin ? <PricesExcelButton /> : null}
      </Stack>
      <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
        <MultiSelect<PriceSourceName, PriceSourceName>
          options={groupedPriceSeries.availablePriceSources}
          getOptionId={(source) => source}
          getOptionLabel={(source) => t(source)}
          values={selectedPriceSources}
          setValues={setSelectedPriceSources}
          small
          selectAll
          labelAbove
          label={t("sources")}
        />
        <MultiSelect<string, string>
          options={groupedPriceSeries.availableMaterialNames}
          getOptionId={(material) => material}
          getOptionLabel={(material) => material}
          values={selectedMaterials}
          setValues={setSelectedMaterials}
          small
          selectAll
          labelAbove
          label={t("materials")}
        />
      </Stack>
      <Grid container spacing={1} mt={2} mx={0}>
        <Grid size={{ xs: 7 }} ml={0}>
          <PricesPlot
            priceSeries={groupedPriceSeries}
            selectedPriceSources={selectedPriceSources}
            selectedMaterials={selectedMaterials}
            setSelectedDate={setSelectedDate}
          />
        </Grid>
        <Grid size={{ xs: 5 }}>
          <TableHeader
            titlePrefix={t("pricesOn")}
            subtitleText={t("priceTableSubtitleText")}
            selectedDate={selectedDate}
            formatDateMonthly={false}
          />
          <PricesTable
            priceSeries={groupedPriceSeries}
            selectedDate={selectedDate}
            selectedPriceSources={selectedPriceSources}
          />
        </Grid>
      </Grid>
    </div>
  );
};

const getDefaultSelectedDate = (
  groupedPriceSeries: GroupedPriceSeries,
  selectedPriceSources: PriceSourceName[]
): Date | null => {
  const defaultSelectedDate = groupedPriceSeries.items
    .filter(([key]) => selectedPriceSources.includes(key.priceSource))
    .map(
      (item) =>
        item[1].validFrom[item[1].validFrom.length - 1]?.toDate() ?? null
    )
    .reduce((max, current) => {
      if (max === null) {
        return current;
      }
      if (current === null) {
        return max;
      }
      return current > max ? current : max;
    }, null);

  return defaultSelectedDate ?? null;
};

const PricesExcelButton = () => {
  const { startDate, endDate } = useDateRange();
  const tenantName = useTenant();
  const [trigger] =
    backendApi.endpoints.getStrategyDashboardPrices.useLazyQuery();
  const start = startDate.format();
  const end = endDate.format();
  const getBlob = () =>
    downloadAsExcel(trigger, {
      tenantName,
      start,
      end,
      "Cache-Control": "no-cache",
    });
  return <ExcelDownloadButton variant="outlined" getBlob={getBlob} />;
};
